import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const NumberModal = ({ number, type, isOpen, onClose }) => {
  if (!isOpen || !number) return null;

  const getNumberInfo = (num, type) => {
    const baseInfo = {
      1: {
        title: "Liderazgo e Independencia",
        description: "Energía de iniciativa, independencia y liderazgo natural. Representa la capacidad de tomar decisiones y dirigir.",
        keywords: ["Independencia", "Liderazgo", "Iniciativa", "Decisión"],
        advice: "Es momento de tomar el control y liderar con confianza."
      },
      2: {
        title: "Colaboración y Escucha",
        description: "Vibración de cooperación, diplomacia y sensibilidad. Favorece el trabajo en equipo y la escucha activa.",
        keywords: ["Cooperación", "Diplomacia", "Sensibilidad", "Equipo"],
        advice: "Conecta con otros y escucha antes de actuar."
      },
      3: {
        title: "Expresión y Creatividad",
        description: "Energía de comunicación, creatividad y expresión artística. Ideal para proyectos creativos y comunicación.",
        keywords: ["Comunicación", "Creatividad", "Expresión", "Arte"],
        advice: "Expresa tus ideas y conecta con tu creatividad."
      },
      4: {
        title: "Orden y Ejecución",
        description: "Vibración de estabilidad, organización y trabajo duro. Favorece la estructura y la disciplina.",
        keywords: ["Estabilidad", "Organización", "Disciplina", "Estructura"],
        advice: "Organiza y ejecuta con método y perseverancia."
      },
      5: {
        title: "Cambio y Exploración",
        description: "Energía de libertad, aventura y adaptabilidad. Favorece los cambios y nuevas experiencias.",
        keywords: ["Libertad", "Aventura", "Adaptabilidad", "Cambio"],
        advice: "Abrete a nuevas experiencias y cambios."
      },
      6: {
        title: "Cuidado y Responsabilidad",
        description: "Vibración de servicio, responsabilidad y armonía. Ideal para cuidar y nutrir relaciones.",
        keywords: ["Servicio", "Responsabilidad", "Armonía", "Cuidado"],
        advice: "Sirve desde el corazón y nutre tus relaciones."
      },
      7: {
        title: "Profundidad y Estudio",
        description: "Energía de introspección, análisis y sabiduría. Favorece el estudio y la reflexión.",
        keywords: ["Introspección", "Análisis", "Sabiduría", "Reflexión"],
        advice: "Dedica tiempo al estudio y la reflexión interior."
      },
      8: {
        title: "Poder y Logros",
        description: "Vibración de autoridad, logros materiales y poder personal. Ideal para negocios y logros.",
        keywords: ["Autoridad", "Logros", "Poder", "Material"],
        advice: "Ejerce tu poder personal y persigue tus logros."
      },
      9: {
        title: "Cierre y Proyección",
        description: "Energía de finalización, compasión y proyección universal. Favorece el cierre de ciclos.",
        keywords: ["Finalización", "Compasión", "Proyección", "Cierre"],
        advice: "Cierra ciclos y proyecta hacia el futuro."
      },
      11: {
        title: "Inspiración y Visión",
        description: "Número maestro de intuición, inspiración y visión elevada. Conecta con lo espiritual.",
        keywords: ["Intuición", "Inspiración", "Visión", "Espiritual"],
        advice: "Confía en tu intuición y conecta con tu visión superior."
      },
      22: {
        title: "Progreso y Construcción",
        description: "Número maestro de construcción material y progreso. Capacidad de manifestar grandes proyectos.",
        keywords: ["Construcción", "Progreso", "Manifestación", "Proyectos"],
        advice: "Construye en grande y manifiesta tus visiones."
      }
    };

    // Manejar binomios
    let info;
    if (typeof num === 'string' && num.includes(' / ')) {
      const [personal, universal] = num.split(' / ');
      const personalInfo = baseInfo[parseInt(personal)] || baseInfo[1];
      const universalInfo = baseInfo[parseInt(universal)] || baseInfo[1];
      
      info = {
        title: `${personalInfo.title} / ${universalInfo.title}`,
        description: `Personal: ${personalInfo.description} | Universal: ${universalInfo.description}`,
        keywords: [...personalInfo.keywords, ...universalInfo.keywords],
        advice: `Personal: ${personalInfo.advice} | Universal: ${universalInfo.advice}`,
        isBindomial: true,
        personal: parseInt(personal),
        universal: parseInt(universal)
      };
    } else {
      info = baseInfo[num] || baseInfo[1];
    }
    
    const typeContext = {
      'year-binomial': `Tu Año Personal ${num.split(' / ')[0]} es tu tema de aprendizaje y crecimiento este año, mientras que el Año Universal ${num.split(' / ')[1]} es la energía colectiva que afecta a toda la humanidad. La combinación de ambas energías te muestra cómo tu desarrollo personal se alinea con las tendencias globales.`,
      'month-binomial': `Tu Mes Personal ${num.split(' / ')[0]} es tu herramienta principal este mes, mientras que el Mes Universal ${num.split(' / ')[1]} es la energía colectiva que predomina. Juntos te indican cómo enfocar tus proyectos personales dentro del ambiente general del mes.`,
      'day-binomial': `Tu Día Personal ${num.split(' / ')[0]} es tu energía específica para hoy, mientras que el Día Universal ${num.split(' / ')[1]} es el clima energético que afecta a todos. La combinación te muestra cómo aprovechar tu enfoque personal dentro del ambiente colectivo del día.`,
      'week-binomial': `Tu Semana Personal ${num.split(' / ')[0]} marca el talento que predomina esta semana en tu ciclo personal, mientras que la Semana Universal ${num.split(' / ')[1]} es la energía colectiva de esta semana del mes. Juntas te indican cómo usar tu talento personal dentro del clima semanal universal.`,
      'universal-year': `El Año Universal ${num} es la energía colectiva que afecta a toda la humanidad este año. Es como el "clima energético global" que influye en eventos mundiales, tendencias sociales y el ambiente general que todos experimentamos.`,
      'universal-month': `El Mes Universal ${num} es la energía colectiva que predomina este mes. Afecta las tendencias sociales, el ambiente general y las experiencias que compartimos todos.`,
      'universal-day': `El Día Universal ${num} es el clima energético que afecta a todos hoy. Es la influencia colectiva que marca el ambiente general del día.`,
      'personal-year': `Tu Año Personal ${num} es tu tema de aprendizaje y crecimiento este año. Se calcula con tu fecha de nacimiento y te indica qué debes desarrollar y aprender.`,
      'personal-month': `Tu Mes Personal ${num} es tu herramienta principal este mes. Te indica el enfoque que debes tener en tus proyectos y desarrollo personal.`,
      'personal-day': `Tu Día Personal ${num} es tu energía específica para hoy. Te indica el enfoque que debes tener en tus actividades diarias.`,
      'week': `La Semana ${num} marca el talento que predomina esta semana. Es la energía específica de tu ciclo semanal.`
    };

    return {
      ...info,
      context: typeContext[type] || `Este es el número ${num} en su contexto.`
    };
  };

  const numberInfo = getNumberInfo(number, type);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div 
            className="px-6 py-4 text-white relative"
            style={{ background: "var(--color-morado)" }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {numberInfo.isBindomial ? (
                  <div className="flex flex-col items-center justify-between h-20">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-morado bg-white border-2 border-[#d4af37] text-lg">
                      {numberInfo.personal}
                    </div>
                    <div className="w-1 h-6 bg-white/60 rounded-full" />
                    <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-morado bg-white border-2 border-[#d4af37] text-lg">
                      {numberInfo.universal}
                    </div>
                  </div>
                ) : (
                <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-morado bg-white border-2 border-[#d4af37] text-xl">
                  {number}
                </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold">{numberInfo.title}</h3>
                  <p className="text-sm opacity-90">{numberInfo.context}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            <div className="mb-4">
              <p className="text-gray-700 leading-relaxed mb-4">{numberInfo.description}</p>
              
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-morado mb-2">Palabras Clave</h4>
                <div className="flex flex-wrap gap-2">
                  {numberInfo.keywords.map((keyword, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 rounded-full text-xs font-medium"
                      style={{ 
                        background: "rgb(var(--color-morado-rgb) / 0.1)",
                        color: "var(--color-morado)",
                        border: "1px solid #d4af37"
                      }}
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
              
            </div>
            
            <button
              onClick={onClose}
              className="w-full py-3 px-4 rounded-xl text-white transition font-medium"
              style={{ background: "var(--color-morado)" }}
            >
              Entendido
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NumberModal;
